import { useEffect, useState } from "react";
import { Crown, Moon, Star, Wand2, Flower2, Castle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../store/lang";

const ICONS = [Crown, Moon, Star, Wand2, Flower2, Castle];

const BLESSINGS = [
  "May her crown always be kindness",
  "May the moon guard her dreams",
  "May she always reach for stars",
  "May magic follow her everywhere",
  "May she bloom in every season",
  "May her life be a fairytale castle",
];

const BLESSINGS_UR = [
  "اس کا تاج ہمیشہ نیکی ہو",
  "چاند اس کے خوابوں کی حفاظت کرے",
  "وہ ہمیشہ ستاروں کو چھوئے",
  "جادو ہر قدم پر اس کے ساتھ ہو",
  "وہ ہر موسم میں کھلتی رہے",
  "اس کی زندگی فیری ٹیل محل بن جائے",
];

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

const newDeck = () =>
  shuffle(ICONS.flatMap((Icon, i) => [{ key: `${i}-a`, pair: i, Icon }, { key: `${i}-b`, pair: i, Icon }]));

export const MemoryMatch = () => {
  const { ur } = useLang();
  const [deck, setDeck] = useState(newDeck);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [won, setWon] = useState(false);

  useEffect(() => {
    if (flipped.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = flipped;
      if (deck[a].pair === deck[b].pair) {
        setMatched((m) => [...m, deck[a].pair]);
        toast.success((ur ? BLESSINGS_UR : BLESSINGS)[deck[a].pair], { duration: 2500 });
        setFlipped([]);
      } else {
        const t = setTimeout(() => setFlipped([]), 800);
        return () => clearTimeout(t);
      }
    }
  }, [flipped, deck]);

  useEffect(() => {
    if (matched.length === ICONS.length) {
      setWon(true);
      toast.success(ur ? "ہایا نور جمال کے لیے تمام دعائیں کھل گئیں!" : "All blessings unlocked for Haya Noor Jamal!");
    }
  }, [matched]);

  const reset = () => {
    setDeck(newDeck());
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setWon(false);
  };

  return (
    <div data-testid="memory-match-game">
      <div className="flex items-center justify-between mb-4 text-sm">
        <span className="text-slate-400">
          {ur ? "چالیں:" : "Moves:"} <span data-testid="memory-moves" className="text-amber-100 tabular-nums">{moves}</span>
        </span>
        <button
          data-testid="memory-reset-btn"
          onClick={reset}
          className="flex items-center gap-1.5 text-amber-200/80 hover:text-amber-100 transition-colors duration-300"
        >
          <RotateCcw size={14} /> {ur ? "دوبارہ ملائیں" : "Shuffle again"}
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2.5 sm:gap-3 max-w-md mx-auto" style={{ perspective: "900px" }}>
        {deck.map((card, i) => {
          const isUp = flipped.includes(i) || matched.includes(card.pair);
          return (
            <button
              key={card.key}
              data-testid={`memory-card-${i}`}
              onClick={() => {
                if (isUp || flipped.length === 2) return;
                setFlipped((f) => [...f, i]);
              }}
              className="relative aspect-square"
              aria-label={`Memory card ${i + 1}`}
            >
              <div className={`card-3d absolute inset-0 ${isUp ? "[transform:rotateY(180deg)]" : ""}`}>
                <div className="card-face absolute inset-0 rounded-xl bg-gradient-to-br from-[#1d1436] to-[#0d0f22] border border-amber-200/25 flex items-center justify-center">
                  <span className="text-amber-300/50 text-lg">✦</span>
                </div>
                <div className="card-face card-back absolute inset-0 rounded-xl bg-gradient-to-br from-amber-200/20 to-rose-200/10 border border-amber-300/50 flex items-center justify-center shadow-[0_0_18px_rgba(212,175,55,0.3)]">
                  <card.Icon size={26} className="text-amber-200" />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {won && (
        <p data-testid="memory-win-message" className={`mt-5 text-center text-2xl text-rose-200 ${ur ? "font-urdu" : "font-script"}`} dir={ur ? "rtl" : "ltr"}>
          {ur ? "اب ہر دعا ننھی ہایا کی حفاظت کرے گی" : "Every blessing now watches over little Haya"}
        </p>
      )}
    </div>
  );
};
